import { logError, logWarning } from "@lib/logger";
import { selectSelectedEntity } from "@store/map/mapReducer";
import { selectMenuItem } from "@store/menuItem/menuItemReducer";
import { useAppSelector } from "@store/store";
import { useMap } from "@vis.gl/react-google-maps";
import { FeatureCollection } from "geojson";
import { FunctionComponent, memo, useEffect, useState } from "react";

const getFileType = (url: string): "geojson" | "kml" | "kmz" => {
  let pathname = url;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {}
  if (pathname.endsWith(".kmz")) {
    return "kmz";
  }
  if (pathname.endsWith(".kml")) {
    return "kml";
  }
  return "geojson";
};

const MapLayersBase: FunctionComponent = () => {
  const menuItem = useAppSelector(selectMenuItem);
  const selectedEntity = useAppSelector(selectSelectedEntity);

  return (
    <>
      {menuItem.mapLayerKmlUrl && (
        <MapLayer url={menuItem.mapLayerKmlUrl} key={menuItem.mapLayerKmlUrl} />
      )}
      {VisitWidget.settings.mapLayerUrl && (
        <MapLayer
          url={VisitWidget.settings.mapLayerUrl}
          key={VisitWidget.settings.mapLayerUrl}
        />
      )}
      {selectedEntity?.entity.mapLayerKmlUrl && (
        <MapLayer
          url={selectedEntity.entity.mapLayerKmlUrl}
          key={selectedEntity.entity.mapLayerKmlUrl}
        />
      )}
    </>
  );
};

const MapLayer: FunctionComponent<{
  url: string;
}> = ({ url }) => {
  const [data, setData] = useState<FeatureCollection | null>(null);
  const map = useMap();
  const fileType = getFileType(url);

  useEffect(() => {
    if (fileType !== "geojson") {
      return;
    }

    const fetchMapLayerData = async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          logWarning("Failed to fetch map layer data", { url });
          return;
        }

        const jsonData = await response.json();
        setData(jsonData);
      } catch (error) {
        logError("Error fetching map layer data", { url, error });
      }
    };

    fetchMapLayerData();
  }, [fileType, url]);

  useEffect(() => {
    if (!map || (fileType !== "kml" && fileType !== "kmz")) {
      return;
    }

    let layer: google.maps.KmlLayer | google.maps.Data | null = null;
    layer = new google.maps.KmlLayer({
      url,
      map,
      preserveViewport: true,
    });

    return () => {
      if (layer instanceof google.maps.KmlLayer) {
        layer.setMap(null);
      }
    };
  }, [map, url, fileType]);

  useEffect(() => {
    if (!map || !data) {
      return;
    }

    const dataLayer = new google.maps.Data({ map });
    dataLayer.addGeoJson(data);

    dataLayer.forEach((feature) => {
      dataLayer.setStyle(getStyle(feature));
    });

    return () => {
      dataLayer.setMap(null);
    };
  }, [map, data]);

  return <></>;
};

interface GeoJSONStyle {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
}

const getFeatureProperty = (
  feature: google.maps.Data.Feature,
  properties: string[],
  type: "number" | "string",
): string | number | undefined => {
  let value: string | number | undefined;
  for (let i = 0; i < properties.length; i++) {
    const property = properties[i];
    value = feature.getProperty(property) as string | number | undefined;
    if (value !== undefined && value !== null) {
      break;
    }
  }

  if (value === undefined || value === null) {
    return;
  }

  if (type === "number") {
    return Number(value);
  }
  return String(value);
};

const getStyle = (feature: google.maps.Data.Feature): GeoJSONStyle => {
  const fillColor = getFeatureProperty(
    feature,
    ["fillColor", "fill"],
    "string",
  ) as string | undefined;
  const fillOpacity = getFeatureProperty(
    feature,
    ["fillOpacity", "fill-opacity"],
    "number",
  ) as number | undefined;
  const strokeColor = getFeatureProperty(
    feature,
    ["strokeColor", "stroke"],
    "string",
  ) as string | undefined;
  const strokeOpacity = getFeatureProperty(
    feature,
    ["strokeOpacity", "stroke-opacity"],
    "number",
  ) as number | undefined;
  const strokeWeight = getFeatureProperty(
    feature,
    ["strokeWeight", "stroke-width"],
    "number",
  ) as number | undefined;

  const result = {} as GeoJSONStyle;

  if (fillColor) {
    result.fillColor = fillColor;
  }

  if (fillOpacity) {
    result.fillOpacity = fillOpacity;
  }

  if (strokeColor) {
    result.strokeColor = strokeColor;
  }

  if (strokeOpacity) {
    result.strokeOpacity = strokeOpacity;
  }

  if (strokeWeight) {
    result.strokeWeight = strokeWeight;
  }

  return result;
};

const MapLayers = memo(MapLayersBase);

export { MapLayers };
